// src/components/panels/useDiscussionComposer.js
"use client";

import { useState, useCallback, useMemo } from "react";
import { auth } from "@/firebase/config";
import { useAuth } from "@/context/AuthContext";
import { uploadProcessedImage } from "@/lib/uploadImageWebp";
import { v4 as uuidv4 } from "uuid";

// simple hashtag + url extraction
const HASHTAG_RE = /(^|\s)#([A-Za-z0-9_]{2,50})\b/g;
const URL_RE = /\bhttps?:\/\/[^\s<>()]+/gi;

function extractHashtags(text) {
  const seen = new Set();
  const orig = [];
  const lower = [];
  let m;
  while ((m = HASHTAG_RE.exec(text)) !== null) {
    const tag = m[2];
    const lc = tag.toLowerCase();
    if (!seen.has(lc)) {
      seen.add(lc);
      orig.push(tag);
      lower.push(lc);
    }
  }
  return { hashtags: orig, hashtagsLower: lower };
}

function extractUrls(text) {
  const urls = [];
  const seen = new Set();
  const matches = text.match(URL_RE) || [];
  for (const u of matches) {
    if (!seen.has(u)) {
      seen.add(u);
      urls.push({ url: u });
    }
  }
  return { urls };
}

const HTML_ESCAPE = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#039;",
};

function escapeHtml(str = "") {
  return String(str).replace(/[&<>"']/g, (ch) => HTML_ESCAPE[ch] || ch);
}

function debugAgentResults(agentResults) {
  if (process.env.NODE_ENV === "production") return;
  if (!Array.isArray(agentResults) || agentResults.length === 0) return;
  if (typeof window === "undefined") return;
  agentResults.forEach((result, index) => {
    const debugPayload = result?.middleware?.data?.debug;
    if (!debugPayload) return;
    const pretty = JSON.stringify(result, null, 2);
    const html = `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>Agent Debug ${index + 1}</title>
    <style>
      body { margin: 0; padding: 0; background: #0f172a; color: #e2e8f0; }
      pre {
        margin: 0;
        padding: 16px;
        white-space: pre-wrap;
        word-break: break-word;
        font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
        font-size: 13px;
        line-height: 1.45;
      }
    </style>
  </head>
  <body>
    <pre>${escapeHtml(pretty)}</pre>
  </body>
</html>`;
    const url = `data:text/html;charset=utf-8,${encodeURIComponent(html)}`;
    window.open(url, "_blank", "noopener");
  });
}

export function useDiscussionComposer({
  mode,        // "post" | "comment" | "reply"
  hubSlug,
  threadId,
  parentId,
  onSubmitted,
}) {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]); // File[]
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [linkPreview, setLinkPreview] = useState(null);

  const canSubmit = useMemo(() => {
    const authed = !!(user || auth.currentUser);
    const hasText = !!text.trim();
    if (!authed || !hasText) return false;

    if (mode === "post") return !!hubSlug;
    // comments/replies require both hubSlug & threadId
    return !!hubSlug && !!threadId;
  }, [user, text, mode, hubSlug, threadId]);

  const addFiles = useCallback((newFiles) => {
    if (!newFiles?.length) return;
    const next = [...files];
    for (const f of newFiles) {
      if (!next.some(n => n.name === f.name && n.size === f.size && n.lastModified === f.lastModified)) {
        next.push(f);
      }
    }
    setFiles(next);
  }, [files]);

  const removeFile = useCallback((idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const clearFiles = useCallback(() => setFiles([]), []);

  const submit = useCallback(async () => {
    const u = user || auth.currentUser;
    if (!u || !canSubmit) return;

    setSubmitting(true);
    setError("");

    try {
      const idToken = await u.getIdToken(true);
      const normalized = text.replace(/\r\n?/g, "\n").replace(/^\t+/gm, "");
      const trimmed = normalized.trim();
      const { hashtags, hashtagsLower } = extractHashtags(trimmed);
      const { urls } = extractUrls(trimmed);

      if (mode === "post") {
        // ---- Optimistic: pending shimmer in feeds ----
        const tempId = uuidv4();
        window.dispatchEvent(new CustomEvent("post:pending", {
          detail: { tempId, hubSlug }
        }));

        // ---- Upload first (client → WEBP), then create ----
        const preThreadId = uuidv4();

        const uploaded = [];
        for (const f of files) {
          const key = uuidv4();
          const { path: storagePath } = await uploadProcessedImage({
            file: f,
            hubSlug,
            threadId: preThreadId,
            objectKey: key,
            maxWidth: 800,
            maxHeight: 500,
          });
          uploaded.push({ storagePath });
        }

        const resp = await fetch(process.env.NEXT_PUBLIC_CREATE_THREAD_API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            idToken,
            hubSlug,
            clientThreadId: preThreadId,
            text: trimmed,
            hashtags,
            hashtagsLower,
            urls,
            images: uploaded,
            linkPreview: uploaded.length === 0 ? linkPreview : null,
          }),
        });
        if (!resp.ok) throw new Error(await resp.text());
        const json = await resp.json().catch(() => ({}));
        const { threadId: createdId, agentResults } = json || {};
        const finalThreadId = createdId || preThreadId;

        if (Array.isArray(agentResults) && agentResults.length) {
          debugAgentResults(agentResults);
          if (agentResults.some((result) => !result?.middleware?.ok)) {
            window.dispatchEvent(new CustomEvent("app:toast", {
              detail: { kind: "error", message: "Agent reply failed to generate." },
            }));
          }
        }

        // resolve shimmer + ask feed to scroll
        window.dispatchEvent(new CustomEvent("post:resolve", { detail: { tempId } }));
        window.dispatchEvent(new CustomEvent("post:await", { detail: { threadId: finalThreadId } }));

        // optional toast
        window.dispatchEvent(new CustomEvent("app:toast", {
          detail: { kind: "success", message: "Your post has been published." }
        }));

        onSubmitted?.({ threadId: finalThreadId });
        setText(""); setFiles([]); setLinkPreview(null);
        return { threadId: finalThreadId, hubSlug };
      }

      const preReplyId = uuidv4();
      const uploaded = [];
      for (const f of files) {
        const key = `replies/${preReplyId}/${uuidv4()}`;
        const { path: storagePath } = await uploadProcessedImage({
          file: f,
          hubSlug,
          threadId,
          objectKey: key,
          maxWidth: 800,
          maxHeight: 500,
        });
        uploaded.push({ storagePath });
      }

      // ---- comment (L0) or reply (L1+) ----
      const resp = await fetch(process.env.NEXT_PUBLIC_CREATE_REPLY_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idToken,
          hubSlug,
          threadId,
          clientReplyId: preReplyId,
          body: trimmed,
          parentId: mode === "comment" ? null : parentId || null,
          hashtags,
          hashtagsLower,
          urls,
          images: uploaded,
          linkPreview: uploaded.length === 0 ? linkPreview : null,
        }),
      });
      if (!resp.ok) throw new Error(await resp.text());
      const json = await resp.json().catch(() => ({}));
      const { replyId, agentResults } = json || {};

      if (Array.isArray(agentResults) && agentResults.length) {
        debugAgentResults(agentResults);
        if (agentResults.some((result) => !result?.middleware?.ok)) {
          window.dispatchEvent(new CustomEvent("app:toast", {
            detail: { kind: "error", message: "Agent reply failed to generate." },
          }));
        }
      }

      // toast + request panel close
      window.dispatchEvent(new CustomEvent("app:toast", {
        detail: { kind: "success", message: "Your reply has been published." }
      }));
      window.dispatchEvent(new CustomEvent("thread-panel:close"));

      // let any listeners update counts, etc.
      window.dispatchEvent(new CustomEvent("reply:created", {
        detail: { hubSlug, threadId, replyId, parentId: mode === "comment" ? null : (parentId || null) }
      }));

      onSubmitted?.({ replyId });
      setText(""); setFiles([]); setLinkPreview(null);
      return { replyId, hubSlug, threadId, parentId: mode === "comment" ? null : (parentId || null) };
    } catch (e) {
      const msg = (e?.message || "").toLowerCase();
      if (msg.includes("permission")) setError("You don’t have permission to post in this space.");
      else if (msg.includes("network") || !navigator.onLine) setError("No internet connection.");
      else setError("Couldn’t publish. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [mode, hubSlug, threadId, parentId, text, files, canSubmit, onSubmitted, user, linkPreview]);

  return {
    text, setText,
    files, addFiles, removeFile, clearFiles,
    submitting, error, canSubmit,
    submit,
    linkPreview,
    setLinkPreview,
  };
}
